// =============================================================
//  scope.js -- real-time oscilloscope display for synth cues
//
//  Renders a time-domain trace driven by a Web Audio AnalyserNode
//  into an SVG element in the score.  Shares the target resolution,
//  handle registry, and cursor infrastructure with waveform.js.
//
//  Unlike audio peak contours (pre-computed from a buffer), the
//  scope is continuously updated at display framerate.
//
//  Lifecycle:
//    prime   -> renderScope with static wave shape (assignCues)
//    start   -> startScope(handle, analyserNode) begins RAF loop
//    stop    -> stopScope(handle) stops RAF, restores static wave
//    cleanup -> destroyWaveform(uid) removes SVG group
//
//  Exports:
//    renderScope(svg, target, uid, opts)
//    startScope(handle, analyserNode)
//    stopScope(handle)
//    resizeScope(handle, targetEl)
// =============================================================

import {
  SVG_NS_EXPORT as SVG_NS,
  resolveTarget,
  getWaveform,
  registerHandle
} from "./waveform.js";
import { applyVizLine } from "./vizStyle.js";
import {
  VIEWS, FREQUENCY_VIEWS, wavePoints, polarPoints, phasePoints, xyPoints, spectrumPoints, pointsAttr, turnPoints,
  ridgeCanvas, paintTrail, showSurface, stageFrameOf, onScreen, phaseLag, cyclePeriod, followLevel, easeIntoSpill, SPILL,
  densityOf,
} from "./scopeViews.js";
import { analyserFor } from "../cues/audio/liveAnalysers.js";
import {
  RIDGE_VIEWS, RIDGE_ROWS, RIDGE_SPAN, ridgeLayout, waveRow, spectrumRow, ridgeFill,
  showRidge, paintRidgeSvg, paintRidgeCanvas,
} from "./scopeRidge.js";
import { barcodeBars, paintBarcode, paintBarcodeLevels, barcodeBetween, BARCODE_COLUMNS, SCROLLS, scrollOn, paintBarcodeScroll, easeStep, barcodeLevels, barcodeSeed } from "./scopeBarcode.js";
import { beatAt, gridNow } from "../cues/audio/beatGrid.js";
import { secs } from "../cues/beats.js";
import { paintGeometry, dropGeometry } from "./boxFx.js";
import { evalMaybeRandom } from "../cues/audio/audioShared.js";
import { transport } from "./transportState.js";

/**
 * viz(s3, spin:) for one voice: seconds a turn (- the other way), 0 none.
 * spin:20 every voice alike; spin:[20, -20] voice by voice (s3__0 the
 * first, round again); spin:birand(10, 30) / choose(20, -20) a draw for
 * each voice, kept while the setting is. Beats, read as seconds each frame.
 */
function voiceSpin(handle, box) {
  const src = box?.vizSpin;
  if (!src) return 0;
  if (handle._spinSrc !== src) {
    handle._spinSrc = src;
    let v;
    try { v = JSON.parse(src); } catch { v = Number(src); }
    if (Array.isArray(v) && v.length) v = v[Number(/__(\d+)$/.exec(handle.uid ?? "")?.[1] ?? 0) % v.length];
    handle._spinBeats = Number(evalMaybeRandom(v)) || 0;
  }
  return handle._spinBeats ? secs(handle._spinBeats) : 0;
}


/** Two reads of one analyser the same frame: no audio block between them. */
function sameFrame(a, b) {
  const n = a.length;
  return a[0] === b[0] && a[n >> 1] === b[n >> 1] && a[n - 1] === b[n - 1];
}

/** The square in the middle of a box, its shorter side. */
function evenSquare(d) {
  const s = Math.min(d.width, d.height);
  return { x: d.x + (d.width - s) / 2, y: d.y + (d.height - s) / 2, width: s, height: s };
}

/** Default scope trace color -- cool teal to distinguish from audio */
const SCOPE_COLOR = "#0ac";

/** Cursor overhang above bbox top (SVG units) */
const CURSOR_OVERHANG = 10;


// =============================================================
//  Static wave shape generator (primed state)
// =============================================================

/**
 * Whether a scope shows its primed static wave when not running. A score
 * element does: the picture of what it will play. A box the live console
 * made does not -- there the wave is what is sounding, and a sine drawn
 * before the first frame or after the voice ends (whatever the view) is a
 * ghost of a sound that is not there.
 */
const primes = (handle, target = handle?._targetEl) =>
  !!handle?._scopeWave && target?.dataset?.liveAudio == null;

/**
 * Generate SVG points string for a static representative waveform.
 * Used at prime time (before synth plays) to show the wave type.
 *
 * @param {string} waveType - "sine", "square", "saw", "triangle", "noise"
 * @param {object} drawBbox - { x, y, width, height }
 * @param {number} cycles   - how many wave cycles to show
 * @returns {string} SVG polyline points
 */
function staticWavePoints(waveType, drawBbox, cycles = 3) {
  const { x, y, width, height } = drawBbox;
  const midY = y + height / 2;
  const halfH = height * 0.4;  // leave some vertical padding
  const samples = Math.min(Math.round(width), 800);
  const step = width / samples;
  const points = [];

  for (let i = 0; i < samples; i++) {
    const t = (i / samples) * cycles * Math.PI * 2;
    let v = 0;

    switch ((waveType || "sine").toLowerCase()) {
      case "sine":
        v = Math.sin(t);
        break;
      case "square":
        v = Math.sin(t) >= 0 ? 1 : -1;
        break;
      case "saw":
      case "sawtooth":
        v = 2 * ((t / (Math.PI * 2)) % 1) - 1;
        break;
      case "triangle":
        v = 2 * Math.abs(2 * ((t / (Math.PI * 2)) % 1) - 1) - 1;
        break;
      case "noise":
      case "white":
        v = Math.random() * 2 - 1;
        break;
      default:
        v = Math.sin(t);
    }

    const px = x + i * step;
    const py = midY - v * halfH;
    points.push(`${px.toFixed(1)},${py.toFixed(1)}`);
  }

  return points.join(" ");
}


// =============================================================
//  Scope rendering
// =============================================================

/**
 * Render a scope display into a target element in the score SVG.
 *
 * Creates an SVG group containing:
 *   - info text label (top)
 *   - scope polyline (real-time or static wave)
 *   - dashed centre line (zero crossing)
 *   - cursor line
 *
 * @param {SVGSVGElement}  svg      - the score SVG root
 * @param {string|Element} target   - "self", element ID, or Element
 * @param {string}         uid      - synth scope uid (for registry)
 * @param {object}         opts
 * @param {Element}        opts.element  - cue element (for "self" resolution)
 * @param {string}         opts.info     - text label
 * @param {string}         opts.wave     - wave type for static primed display
 * @param {string}         opts.color    - scope trace color (default: SCOPE_COLOR)
 * @returns {object|null}  scope handle (compatible with cursor functions)
 */
export function renderScope(svg, target, uid, opts = {}) {
  const targetEl = resolveTarget(svg, target, opts.element);
  if (!targetEl) {
    console.warn(`[scope] Target not found: ${target}`);
    return null;
  }

  // Reuse existing scope if already rendered for this uid
  const existing = getWaveform(uid);
  if (existing?.group?.parentNode) {
    if (opts.info && existing._infoText) {
      existing._infoText.innerHTML = opts.info;  // Use innerHTML for HTML content
      // Update tooltip if visible
      const tooltip = existing._infoText._tooltip;
      if (tooltip && tooltip.style.display !== "none") {
        tooltip.innerHTML = opts.info;
      }
    }
    // Update scope line color if provided and different (text stays black)
    if (opts.color && opts.color !== existing._scopeColor) {
      existing._scopeColor = opts.color;
      if (existing._scopeLine) existing._scopeLine.setAttribute("stroke", opts.color);
      if (existing.cursor) existing.cursor.setAttribute("stroke", opts.color);
      // a style set by viz(uid, stroke:, width:) outlasts a re-run of the synth
      applyVizLine(existing._scopeLine, existing._targetEl);
    }
    // Update background color if provided
    if (opts.backgroundColor !== undefined && existing._borderRect) {
      existing._borderRect.setAttribute("fill", opts.backgroundColor || "none");
    }
    return existing;
  }

  const bbox = targetEl.getBBox();

  // Text padding: info text at top, scope trace below
  // Fixed font size of 12px, not linked to bbox scale
  const fontSize = 12;
  let textPad = 0;
  let infoText = null;
  let fo = null;

  // A short host (a note-sized box) cannot carry two lines of label above
  // its trace — the label would eat the box and leave a sliver. Below that
  // height the trace takes the whole box and the info goes into a tooltip.
  const labelPad = fontSize * 2.4 + 4; // room for 2 lines + gap
  const inlineInfo = !!opts.info && bbox.height >= labelPad * 1.8;
  if (inlineInfo) {
    textPad = labelPad;
  }

  const drawBbox = {
    x: bbox.x,
    y: bbox.y + textPad,
    width: bbox.width,
    height: bbox.height - textPad
  };

  // --- Build SVG group ---
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", "oscilla-scope");
  g.style.pointerEvents = "none";  // Use style instead of attribute for reliable click-through
  g.dataset.uid = uid;
  if (targetEl.dataset?.vizBlend) g.style.mixBlendMode = targetEl.dataset.vizBlend;   // viz(s3, blend:)
  // viz(s3, opacity:) -- a voice come after it starts as faded as the box, not at 1 for a frame
  if (targetEl.getAttribute?.("opacity") != null) g.setAttribute("opacity", targetEl.getAttribute("opacity"));
  if (targetEl.dataset?.vizFilter) g.style.filter = targetEl.dataset.vizFilter;   // viz(s3, blur:, hue:, warp: ...) boxFx.js
  // Back-reference so the DSL extent system can find (and supersede) this
  // scope's own extent visuals for a given cue element.
  if (targetEl.id) g.dataset.scopeTarget = targetEl.id;


  // Scope trace color
  const color = opts.color || SCOPE_COLOR;

  // Info text label via foreignObject (2 lines max, tooltip on hover for full text)
  if (inlineInfo) {
    fo = document.createElementNS(SVG_NS, "foreignObject");
    fo.setAttribute("x", bbox.x);
    fo.setAttribute("y", bbox.y);
    fo.setAttribute("width", bbox.width);
    fo.setAttribute("height", textPad);
    fo.style.pointerEvents = "none";
    fo.style.overflow = "hidden";

    const div = document.createElement("div");
    div.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
    div.style.cssText = [
      `font-family: monospace`,
      `font-size: ${fontSize}px`,
      `color: #000`,
      "overflow: hidden",
      "word-break: break-all",
      "overflow-wrap: anywhere",
      "white-space: normal",
      "line-height: 1.2",
      "padding: 1px 2px",
      "box-sizing: border-box",
      `width: ${bbox.width}px`,
      `max-height: ${textPad}px`,
      "pointer-events: auto",  // Enable hover for tooltip
      "cursor: default"
    ].join(";");
    div.innerHTML = opts.info;

    // Custom tooltip for full DSL with highlighting
    const tooltip = document.createElement("div");
    tooltip.style.cssText = [
      "position: fixed",
      "z-index: 10000",
      "background: #fffef0",
      "border: 1px solid #888",
      "border-radius: 4px",
      "padding: 6px 10px",
      "font-family: monospace",
      `font-size: ${fontSize}px`,
      "color: #000",
      "white-space: pre-wrap",
      "word-break: break-all",
      "max-width: 400px",
      "box-shadow: 2px 2px 8px rgba(0,0,0,0.2)",
      "pointer-events: none",
      "display: none"
    ].join(";");
    document.body.appendChild(tooltip);

    // Store tooltip reference for updates
    div._tooltip = tooltip;

    div.addEventListener("mouseenter", (e) => {
      tooltip.innerHTML = div.innerHTML;  // Copy current highlighted HTML
      tooltip.style.display = "block";
      // Position near cursor
      const rect = div.getBoundingClientRect();
      tooltip.style.left = `${rect.left}px`;
      tooltip.style.top = `${rect.bottom + 4}px`;
    });

    div.addEventListener("mousemove", (e) => {
      // Update position to follow cursor loosely
      tooltip.style.left = `${e.clientX + 10}px`;
      tooltip.style.top = `${e.clientY + 15}px`;
    });

    div.addEventListener("mouseleave", () => {
      tooltip.style.display = "none";
    });

    fo.appendChild(div);
    g.appendChild(fo);
    infoText = div;
  }

  // Dashed centre line (zero crossing reference)
  const centreLine = document.createElementNS(SVG_NS, "line");
  centreLine.setAttribute("x1", drawBbox.x);
  centreLine.setAttribute("y1", drawBbox.y + drawBbox.height / 2);
  centreLine.setAttribute("x2", drawBbox.x + drawBbox.width);
  centreLine.setAttribute("y2", drawBbox.y + drawBbox.height / 2);
  centreLine.setAttribute("stroke", "#000");
  centreLine.setAttribute("stroke-width", "0.3");
  centreLine.setAttribute("opacity", "0.15");
  centreLine.setAttribute("stroke-dasharray", "4,4");
  centreLine.style.pointerEvents = "none";
  if (targetEl?.dataset?.liveAudio != null) centreLine.style.display = "none";

  // Scope trace polyline - full opacity for visibility
  const scopeLine = document.createElementNS(SVG_NS, "polyline");
  scopeLine.setAttribute("fill", "none");
  scopeLine.setAttribute("stroke", color);
  scopeLine.setAttribute("stroke-width", "1");
  scopeLine.setAttribute("stroke-linejoin", "round");
  scopeLine.style.pointerEvents = "none";
  applyVizLine(scopeLine, targetEl);     // a box styled by viz(): every voice drawn in it

  // Render static primed waveform if wave type is known
  if (opts.wave) {
    if (targetEl?.dataset?.liveAudio == null) scopeLine.setAttribute("points", staticWavePoints(opts.wave, drawBbox));
  }

  // Black border rect around scope area (like waveform)
  const borderRect = document.createElementNS(SVG_NS, "rect");
  borderRect.setAttribute("x", drawBbox.x);
  borderRect.setAttribute("y", drawBbox.y);
  borderRect.setAttribute("width", drawBbox.width);
  borderRect.setAttribute("height", drawBbox.height);
  // Background color from playbackClients (if specified)
  if (opts.backgroundColor) {
    borderRect.setAttribute("fill", opts.backgroundColor);
  } else {
    borderRect.setAttribute("fill", "none");
  }
  // a console box shows its sound, not a frame (viz(uid, border:) adds one)
  borderRect.setAttribute("stroke", targetEl?.dataset?.liveAudio != null ? "none" : "#000");
  borderRect.setAttribute("stroke-width", "1");
  borderRect.style.pointerEvents = "none";

  // Cursor line
  const cursor = document.createElementNS(SVG_NS, "line");
  cursor.setAttribute("x1", bbox.x);
  cursor.setAttribute("y1", bbox.y - CURSOR_OVERHANG);
  cursor.setAttribute("x2", bbox.x);
  cursor.setAttribute("y2", bbox.y + bbox.height);
  cursor.setAttribute("stroke", color);
  cursor.setAttribute("stroke-width", "1");
  cursor.setAttribute("opacity", "0.5");
  cursor.style.pointerEvents = "none";
  // a console box has no timeline for a cursor to stand on
  if (targetEl?.dataset?.liveAudio != null) cursor.style.display = "none";

  // Extent line - either find existing sibling in parent group or create new
  const extentY = drawBbox.y + drawBbox.height / 2;
  const defaultExtent = opts.extent || 200;
  let extentLine = null;
  let extentHandle = null;
  let usingExistingExtent = false;
  const parent = targetEl.parentElement;
  // opts.skipExtent: the cue has a DSL extent:N param — the draggable
  // dsl-extent-line (cueDispatcher) owns the extent visual instead.
  // A box the live console made has no extent either: it is not on the
  // timeline, and the line and its handle only clutter the picture.
  const liveBox = targetEl?.dataset?.liveAudio != null;
  const skipExtentLine = !!opts.skipExtent || liveBox;

  // Check if target element has a sibling path/line in a parent group (SVG-authored extent)
  if (!skipExtentLine && parent && parent.tagName?.toLowerCase() === 'g' && parent.id !== 'layer1') {
    // Look for a sibling path or line element
    const siblings = parent.querySelectorAll('path, line');
    for (const sibling of siblings) {
      if (sibling !== targetEl && sibling.id !== targetEl.id) {
        // Found an extent line - use it
        extentLine = sibling;
        usingExistingExtent = true;
        // Ensure proper stroke attributes for animation visibility
        if (!extentLine.getAttribute("stroke") || extentLine.getAttribute("stroke") === "none") {
          extentLine.setAttribute("stroke", color);
        }
        if (!extentLine.getAttribute("stroke-width")) {
          extentLine.setAttribute("stroke-width", "2.5");
        }
        extentLine.setAttribute("stroke-dasharray", "8 4");
        break;
      }
    }
  }

  // If no existing extent line, create one (but not for audio objects)
  if (!extentLine && !skipExtentLine) {
    extentLine = document.createElementNS(SVG_NS, "line");
    extentLine.setAttribute("class", "scope-extent");
    extentLine.setAttribute("x1", bbox.x + bbox.width);
    extentLine.setAttribute("y1", extentY);
    extentLine.setAttribute("x2", bbox.x + bbox.width + defaultExtent);
    extentLine.setAttribute("y2", extentY);
    extentLine.setAttribute("stroke", color);
    extentLine.setAttribute("stroke-opacity", "0.8");
    extentLine.setAttribute("stroke-width", "2.5");
    extentLine.setAttribute("stroke-dasharray", "8 4");
    extentLine.style.pointerEvents = "none";

    // Extent end handle (vertical bar) - only for created lines
    extentHandle = document.createElementNS(SVG_NS, "line");
    extentHandle.setAttribute("class", "scope-extent");
    extentHandle.setAttribute("x1", bbox.x + bbox.width + defaultExtent);
    extentHandle.setAttribute("y1", extentY - 10);
    extentHandle.setAttribute("x2", bbox.x + bbox.width + defaultExtent);
    extentHandle.setAttribute("y2", extentY + 10);
    extentHandle.setAttribute("stroke", color);
    extentHandle.setAttribute("stroke-opacity", "0.8");
    extentHandle.setAttribute("stroke-width", "2.5");
    extentHandle.style.pointerEvents = "none";
  }

  g.appendChild(borderRect);
  g.appendChild(centreLine);
  g.appendChild(scopeLine);
  g.appendChild(cursor);

  // Only append extent elements if we created them (not using existing, and not skipped)
  if (!usingExistingExtent && extentLine) {
    g.appendChild(extentLine);
    if (extentHandle) g.appendChild(extentHandle);
  }

  // --- Insert into SVG ---
  // Same insertion logic as renderWaveform: child of <g>/<svg>,
  // sibling of shapes with transform observer.
  const tagName = targetEl.tagName?.toLowerCase();
  if (tagName === "g" || tagName === "svg") {
    targetEl.appendChild(g);
  } else {
    const parent = targetEl.parentNode;
    if (targetEl.nextSibling) {
      parent.insertBefore(g, targetEl.nextSibling);
    } else {
      parent.appendChild(g);
    }

    // Mirror transform changes from target onto scope group
    const syncTransform = () => {
      const t = targetEl.getAttribute("transform") || "";
      g.setAttribute("transform", t);
    };
    syncTransform();

    const observer = new MutationObserver(syncTransform);
    observer.observe(targetEl, { attributes: true, attributeFilter: ["transform"] });
    g._transformObserver = observer;
  }

  // --- Extent animation state ---
  let extentAnimId = null;
  let extentDashOffset = 0;
  let extentAnimSpeed = 1;

  // --- Build handle ---
  const handle = {
    uid,
    group: g,
    cursor,
    bbox,
    _drawBbox: drawBbox,
    _cursorOverhang: CURSOR_OVERHANG,
    _scopeLine: scopeLine,
    _centreLine: centreLine,
    _borderRect: borderRect,
    _extentLine: extentLine,
    _extentHandle: extentHandle,
    _extentY: extentY,
    _extent: defaultExtent,
    _usingExistingExtent: usingExistingExtent,
    _infoText: infoText,
    _tooltip: infoText?._tooltip || null,
    _fo: fo,
    _fontSize: fontSize,
    _textPad: textPad,
    _scopeColor: color,
    _scopeWave: opts.wave || null,
    _scopeRafId: null,
    _scopeRunning: false,
    _scopeAnalyser: null,
    _targetEl: targetEl,
    _rafId: null,
    _running: false,
    // Stubs so waveform cursor functions don't error
    upperLine: null,
    lowerLine: null,
  };

  // Extent animation methods (no-op if no extent line, e.g. for audio objects)
  handle._startExtentAnimation = function(speed) {
    if (!extentLine || extentAnimId) return;
    extentAnimSpeed = speed ?? transport.speedMultiplier ?? 1;
    const animate = () => {
      // Direction based on playback speed
      // Positive speed: dashes move right (decreasing offset)
      // Negative speed: dashes move left (increasing offset)
      const direction = extentAnimSpeed < 0 ? 0.5 : -0.5;
      extentDashOffset = (extentDashOffset + direction + 12) % 12;
      extentLine.setAttribute("stroke-dashoffset", extentDashOffset);
      extentAnimId = requestAnimationFrame(animate);
    };
    animate();
  };

  handle._stopExtentAnimation = function() {
    if (extentAnimId) {
      cancelAnimationFrame(extentAnimId);
      extentAnimId = null;
      if (extentLine) extentLine.setAttribute("stroke-dashoffset", "0");
    }
  };

  // Update extent length (only for created extent lines, not existing SVG-authored ones)
  handle._setExtent = function(newExtent) {
    if (!extentLine || usingExistingExtent) return; // Don't modify SVG-authored extent
    handle._extent = newExtent;
    const endX = bbox.x + bbox.width + newExtent;
    extentLine.setAttribute("x2", endX);
    if (extentHandle) {
      extentHandle.setAttribute("x1", endX);
      extentHandle.setAttribute("x2", endX);
    }
  };

  // Update extent color (only for created extent lines)
  handle._setExtentColor = function(newColor) {
    if (!extentLine || usingExistingExtent) return; // Don't modify SVG-authored extent color
    extentLine.setAttribute("stroke", newColor);
    if (extentHandle) {
      extentHandle.setAttribute("stroke", newColor);
    }
  };

  // Cleanup tooltip when scope is destroyed
  handle._cleanupTooltip = function() {
    if (infoText?._tooltip) {
      infoText._tooltip.remove();
      infoText._tooltip = null;
    }
  };


  registerHandle(uid, handle);
  return handle;
}


// =============================================================
//  Real-time scope display
// =============================================================

/**
 * Start real-time scope display driven by an AnalyserNode.
 *
 * Reads getFloatTimeDomainData each frame (-1..1) and maps the values
 * to y within the draw bbox, downsampled to the SVG's pixel width.
 *
 * @param {object}       handle   - scope handle from renderScope
 * @param {AnalyserNode} analyser - Web Audio AnalyserNode tapped from synth chain
 */
export function startScope(handle, analyser) {
  if (!handle?._scopeLine || !analyser) return;

  handle._scopeRunning = true;
  handle._scopeAnalyser = analyser;
  handle._fade = null; setTraceOpacity(handle, "");   // a new voice on it: whole again

  const dataArray = new Float32Array(analyser.fftSize);
  let yArray = null;                     // view:xy -- the other sound's frame
  let xCheck = null;                     // ... and this one's, read again (sameBlock)
  const freqArray = new Uint8Array(analyser.frequencyBinCount);
  const sampleRate = analyser.context?.sampleRate || 48000;
  const line = handle._scopeLine;

  // The view is the box's (viz(s3, view:polar) -> data-viz-view), and so is
  // a trail (data-viz-trail), both read every frame: set while it runs, or
  // before a new poly voice starts, they take at once. The drawing box is
  // re-read whenever resizeScope() replaces it.
  let shownView = null, shownSurface = null;

  function tick() {
    if (!handle._scopeRunning) return;
    if (handle._fade) {
      const f = handle._fade;
      setTraceOpacity(handle, String(Math.max(0, 1 - (performance.now() - f.t0) / f.ms).toFixed(3)));
    }
    const draw = handle._drawBbox;
    const box = handle._targetEl?.dataset;
    const view = VIEWS.has(box?.vizView) ? box.vizView : "wave";
    // trail: seconds a trace takes to fade (viz(s3, trail:2)); 0 = none
    const keep = Math.min(30, Math.max(0, Number(box?.vizTrail) || 0));
    // viz(s3, kaleido:6 / mirror:x / tile:3): copies of the box's canvas (boxFx.js)
    const geom = box?.vizGeom || "";
    // a box an animation turns moves under its own trail: paint it on the
    // stage (not with geometry: its copies are about the box's own middle)
    const stage = keep > 0 && !geom ? stageFrameOf(handle) : null;
    // A console box draws on a canvas, trail or not: an SVG trace sweeping
    // a big box is rastered again every frame -- three times the GPU time of
    // a small one; the canvas costs the same at any size. Geometry copies a canvas too.
    const live = box?.liveAudio != null || !!geom || !!box?.vizGlyphs;      // glyphs: are painted, as a canvas trace
    const barcode = view === "barcode";
    const ridge = RIDGE_VIEWS.has(view) || barcode;
    // a barcode is always painted (canvas): bars, no lines to keep
    const surface = ridge ? (live || barcode ? "ridgeCanvas" : "ridge")
      : keep > 0 ? (stage ? "stageTrail" : "trail") : live ? "trail" : "line";
    if (!geom && handle._geom) dropGeometry(handle);
    if (surface !== shownSurface) { showSurface(handle, surface); showRidge(handle, surface); shownSurface = surface; }
    if (handle._detached) stageLook(handle);
    if (view !== shownView) {
      shownView = view;
      // smoothing only shapes frequency data; the trace's time data is raw
      analyser.smoothingTimeConstant = FREQUENCY_VIEWS.has(view) ? 0.6 : 0;
    }

    // only the part on the screen is drawn (a box can be many screens wide)
    const vis = onScreen(handle.group, draw);
    if (!vis) { handle._scopeRafId = requestAnimationFrame(tick); return; }
    if (barcode) {
      const n = Math.max(4, Math.min(512, Math.round(Number(box?.vizRows)) || BARCODE_COLUMNS));
      // step: beats each barcode holds, on the shared grid -- read anew only when a step begins
      const step = Number(box?.vizStep) > 0 ? Number(box.vizStep) : 0;
      const bc = handle._barcode ||= {};
      const beat = step ? beatAt(gridNow().ms) : 0;
      const at = step ? Math.floor(beat / step) : null;
      // tween: the share of each step a change takes (from where the bars are now, eased)
      const tween = step && Number(box?.vizTween) > 0 ? Math.min(1, Number(box.vizTween)) : 0;
      // scroll: a conveyor -- a bar in each step (a sixteenth by default), moving steadily
      const dir = SCROLLS.has(box?.vizScroll) ? box.vizScroll : null;
      if (dir) {
        analyser.getByteFrequencyData(freqArray);
        const fresh = barcodeBars(freqArray, sampleRate, n, bc);
        const every = step || 0.25 / 4;
        const pos = beatAt(gridNow().ms) / every, idx = Math.floor(pos);
        // a strip starting is filled from a first barcode of the levels (bands above their median)
        const strip = scrollOn(bc, fresh, n, idx, bc.strip?.length === n + 1 ? fresh : barcodeSeed(barcodeLevels(freqArray, sampleRate, n)));
        // tween: the strip moves a bar a step, gliding over that share of the step (eased),
        // then resting -- a ticker; without it, at a steady speed
        const glide = Number(box?.vizTween) > 0 ? Math.min(1, Number(box.vizTween)) : 0;
        const tg = glide ? easeStep(Math.min(1, (pos - idx) / glide)) : null;
        const frac = glide ? tg : pos - idx;
        const m = handle.group?.getScreenCTM?.();
        const density = densityOf(m);
        handle._ridgeCv = ridgeCanvas(handle, vis, density);
        // with tween: the bar coming in grows from its middle, the one going out shrinks away
        paintBarcodeScroll(handle._ridgeCv.canvas, vis, draw, strip, frac, dir, line.getAttribute("stroke") || SCOPE_COLOR, tg);
        if (geom) paintGeometry(handle, handle._ridgeCv, draw, geom);
        handle._scopeRafId = requestAnimationFrame(tick);
        return;
      }
      // read every frame (each column's average keeps up with the sound); taken
      // as the barcode only when a step begins -- read once a step, the averages
      // lagged and every column was on
      analyser.getByteFrequencyData(freqArray);
      const fresh = barcodeBars(freqArray, sampleRate, n, bc);
      if (!step || at !== bc.at || bc.on?.length !== n) {
        bc.from = tween && bc.shown?.length === n ? bc.shown : null;
        bc.on = fresh;
        bc.at = at;
      }
      const on = bc.on;
      const m = handle.group?.getScreenCTM?.();
      const density = densityOf(m);
      handle._ridgeCv = ridgeCanvas(handle, vis, density);
      if (tween && bc.from) {
        const t = (beat / step - at) / tween;
        bc.shown = barcodeBetween(bc.from, on, t);
        paintBarcodeLevels(handle._ridgeCv.canvas, vis, draw, bc.shown, line.getAttribute("stroke") || SCOPE_COLOR);
      } else {
        bc.shown = Float32Array.from(on);
        paintBarcode(handle._ridgeCv.canvas, vis, draw, on, line.getAttribute("stroke") || SCOPE_COLOR);
      }
      if (geom) paintGeometry(handle, handle._ridgeCv, draw, geom);
      handle._scopeRafId = requestAnimationFrame(tick);
      return;
    }
    if (ridge) {
      tickRidge(view, box, draw, vis, surface);
      if (geom) paintGeometry(handle, handle._ridgeCv, draw, geom);
      handle._scopeRafId = requestAnimationFrame(tick);
      return;
    }
    // a console box's figure is scaled to the sound's level over a second
    // or two, not to each frame's own peak: it swells past the box on a
    // loud note (onto its canvas's spill) and shrinks as the note dies
    const now = performance.now();
    const dt = (now - (handle._frameT ?? now)) / 1000;
    handle._frameT = now;
    const free = box?.liveAudio != null && !FREQUENCY_VIEWS.has(view);
    // ... and a figure (polar, phase, xy) has even axes, in the square in the
    // box's middle: a circle stays round, and a dense one no longer fills
    // the box out to its edges, drawing its outline
    const fig = free && view !== "wave" ? evenSquare(draw) : draw;
    let pts;
    if (FREQUENCY_VIEWS.has(view)) {
      analyser.getByteFrequencyData(freqArray);
      pts = spectrumPoints(freqArray, draw, sampleRate, vis);
    } else {
      analyser.getFloatTimeDomainData(dataArray);
      // view:xy -- y is another sound (viz(s1, view:xy, y:s2)); until it
      // plays, this one against itself a quarter cycle on (phase)
      const yA = view === "xy" ? analyserFor(box?.vizY) : null;
      if (yA) {
        if (yArray?.length !== yA.fftSize) yArray = new Float32Array(yA.fftSize);
        if (xCheck?.length !== dataArray.length) xCheck = new Float32Array(dataArray.length);
        yA.getFloatTimeDomainData(yArray);
        // The two frames must be the same moment's. An analyser's frame moves
        // on a block (128 samples) at a time, on the audio thread: one block
        // landing between the two reads put y 128 samples ahead of x, and the
        // figure jumped to another shape for a frame -- a Lissajous every few
        // seconds. x read again unchanged: no block came between them.
        for (let tries = 0; tries < 3; tries++) {
          analyser.getFloatTimeDomainData(xCheck);
          if (sameFrame(xCheck, dataArray)) break;
          dataArray.set(xCheck);
          yA.getFloatTimeDomainData(yArray);
        }
      }
      const ref = free ? followLevel(handle, "_level", dataArray, dt) : 0;
      const refY = free && yA ? followLevel(handle, "_levelY", yArray, dt) : 0;
      pts = yA ? xyPoints(dataArray, yArray, fig, ref, refY)
        : view === "xy" ? phasePoints(dataArray, fig, Math.max(1, Math.round(0.25 * cyclePeriod(dataArray, handle))), ref)
        : view === "polar" ? polarPoints(dataArray, fig, ref)
        : view === "phase" ? phasePoints(dataArray, fig, phaseLag(box, dataArray, handle), ref)
        : wavePoints(dataArray, draw, vis, ref);
    }
    // viz(s3, spin:) -- the voice's trace turning round the box's middle
    const spin = voiceSpin(handle, box);
    if (spin) handle._spinAng = (handle._spinAng ?? 0) + 2 * Math.PI * (now - (handle._spinT ?? now)) / 1000 / spin;
    handle._spinT = now;
    if (pts && spin) pts = turnPoints(pts, draw, handle._spinAng);
    if (pts && free) pts = easeIntoSpill(pts, fig, SPILL, view !== "wave");
    // the trace as it is drawn, in the box's units, for text running along it (textAlong.js)
    if (pts) handle._lastPts = pts;
    if (pts) {
      if (surface !== "line") {
        paintTrail(handle, pts, stage ? draw : vis, keep, line.getAttribute("stroke") || SCOPE_COLOR,
          Number(box?.vizWidth) || 1.5, stage, free ? SPILL : 0, box?.vizGlyphs || null);
      } else line.setAttribute("points", pointsAttr(pts));
    }
    if (geom) paintGeometry(handle, handle._trail, draw, geom);

    handle._scopeRafId = requestAnimationFrame(tick);
  }

  // view:ridge / waterfall -- a line taken every span/rows seconds, the
  // stack moved every frame (scopeRidge.js). The lines are the handle's:
  // a new voice in the box starts its own.
  function tickRidge(view, box, draw, vis, surface) {
    const st = handle._ridge ||= { rows: [], last: 0, view: null, sig: "" };
    const rows = Math.max(2, Math.min(120, Math.round(Number(box?.vizRows)) || RIDGE_ROWS));
    const span = Number(box?.vizSpan) > 0 ? Number(box.vizSpan) : secs(RIDGE_SPAN);
    const lay = ridgeLayout(view, draw, rows);
    // another view or box: the lines start again
    const sig = `${view},${rows},${draw.x},${draw.y},${draw.width},${draw.height}`;
    if (st.sig !== sig) {
      st.sig = sig; st.rows = []; st.last = 0; st.peak = 0;
      handle._ridgeG?.replaceChildren();
    }
    st.max = rows;
    const every = span / rows, now = performance.now() / 1000;
    if (!st.last || now - st.last >= every) {
      let row;
      if (view === "waterfall") {
        analyser.getByteFrequencyData(freqArray);
        row = spectrumRow(freqArray, draw, lay.amp, sampleRate);
      } else {
        analyser.getFloatTimeDomainData(dataArray);
        row = waveRow(dataArray, draw, lay.amp, st);
      }
      st.rows.push(row);
      if (st.rows.length > rows) st.rows.splice(0, st.rows.length - rows);
      st.fresh = true;
      st.fill = ridgeFill(handle._targetEl, handle.group);     // looked up a line, not a frame
      // on the grid of lines, unless a frame came late enough to miss one
      st.last = st.last && now - st.last < 2 * every ? st.last + every : now;
    }
    const frac = Math.min(1, Math.max(0, (now - st.last) / every));
    const style = {
      color: line.getAttribute("stroke") || SCOPE_COLOR,
      width: Number(box?.vizWidth) || 1.5,
      fill: st.fill,
    };
    if (surface === "ridge") paintRidgeSvg(handle, st, lay, frac, style);
    else {
      const m = handle.group?.getScreenCTM?.();
      const density = densityOf(m);
      handle._ridgeCv = ridgeCanvas(handle, vis, density);
      paintRidgeCanvas(handle._ridgeCv.canvas, vis, st, lay, frac, style);
    }
  }

  if (handle._scopeRafId) cancelAnimationFrame(handle._scopeRafId);
  tick();      // the first frame now: no frame of anything else in between
}


/** The trace's own opacity -- line or trail -- under the box's (viz(opacity:)). */
function setTraceOpacity(handle, o) {
  handle._traceOpacity = o === "" ? null : o;      // "": whole
  for (const el of [handle._scopeLine, handle._trail?.fo]) {
    if (el && el.style.opacity !== o) el.style.opacity = o;
  }
}

/**
 * A surface painted on the stage (a trail under a rotate()) is outside the
 * box's group, so the box's opacity, blend and effects do not reach it: given here,
 * each frame -- viz(opacity:follow(noise.fader)) fades it with the rest.
 */
function stageLook(handle) {
  const box = handle._targetEl;
  const o = String(Number(box?.getAttribute("opacity") ?? 1) * Number(handle._traceOpacity ?? 1));
  const blend = box?.dataset?.vizBlend || "";
  const filter = box?.dataset?.vizFilter || "";
  for (const el of handle._detached) {
    if (el.style.opacity !== o) el.style.opacity = o;
    if (el.style.mixBlendMode !== blend) el.style.mixBlendMode = blend;
    if (el.style.filter !== filter) el.style.filter = filter;
  }
}

/**
 * Fade the trace out over `secs` as it goes on drawing -- a voice's
 * release (synth.js stops it at the end).
 */
export function fadeScope(handle, secs) {
  if (!handle) return;
  handle._fade = { t0: performance.now(), ms: Math.max(1, secs * 1000) };
}

/**
 * Stop the real-time scope display.
 * Restores the static primed waveform shape.
 *
 * @param {object} handle - scope handle from renderScope
 */
export function stopScope(handle) {
  if (!handle) return;

  handle._scopeRunning = false;
  // a primed shape shows whole; a trail left to be stays as faded as it was
  handle._fade = null;
  if (handle._scopeLine) handle._scopeLine.style.opacity = "";
  handle._traceOpacity = null;
  if (handle._scopeRafId) {
    cancelAnimationFrame(handle._scopeRafId);
    handle._scopeRafId = null;
  }
  handle._scopeAnalyser = null;

  // Restore static wave shape if one was primed; a console box goes blank
  if (handle._scopeLine && primes(handle) && handle._drawBbox) {
    handle._scopeLine.setAttribute("points",
      staticWavePoints(handle._scopeWave, handle._drawBbox));
  } else if (handle._scopeLine) {
    handle._scopeLine.setAttribute("points", "");
  }
  // a console box draws on its canvas (tick): blank that too, unless it
  // is a trail, which is left to be
  if (!(Number(handle._targetEl?.dataset?.vizTrail) > 0)) {
    const c = handle._trail?.canvas;
    c?.getContext("2d").clearRect(0, 0, c.width, c.height);
  }
}


/**
 * Resize a scope to match its target element's new dimensions.
 * Call this after the target element has been resized.
 * Redraws all scope elements at the new scale.
 *
 * @param {object} handle    - scope handle from renderScope
 * @param {Element} [targetEl] - the target element (optional, uses stored ref)
 */
export function resizeScope(handle, targetEl) {
  if (!handle) return;

  const target = targetEl || handle._targetEl;
  if (!target) return;

  const bbox = target.getBBox();
  const textPad = handle._textPad || 0;

  // Guard against dimensions too small for the text padding
  const minHeight = textPad + 20; // Need at least 20px for waveform area
  if (bbox.height < minHeight || bbox.width < 20) {
    console.warn(`[scope] resizeScope: dimensions too small (${bbox.width}x${bbox.height}), min height=${minHeight}`);
    return;
  }

  // Update stored bbox
  handle.bbox = bbox;

  // Update draw bbox (ensure non-negative dimensions)
  const drawBbox = {
    x: bbox.x,
    y: bbox.y + textPad,
    width: Math.max(20, bbox.width),
    height: Math.max(20, bbox.height - textPad)
  };
  handle._drawBbox = drawBbox;

  // Clear any transform on the group (we're repositioning everything absolutely)
  if (handle.group) {
    handle.group.removeAttribute("transform");
  }

  // Update foreignObject for info text
  if (handle._fo) {
    handle._fo.setAttribute("x", bbox.x);
    handle._fo.setAttribute("y", bbox.y);
    handle._fo.setAttribute("width", bbox.width);
    handle._fo.setAttribute("height", textPad);
    if (handle._infoText) {
      handle._infoText.style.width = `${bbox.width}px`;
      handle._infoText.style.maxHeight = `${textPad}px`;
    }
  }

  // Update border rect
  if (handle._borderRect) {
    handle._borderRect.setAttribute("x", drawBbox.x);
    handle._borderRect.setAttribute("y", drawBbox.y);
    handle._borderRect.setAttribute("width", drawBbox.width);
    handle._borderRect.setAttribute("height", drawBbox.height);
  }

  // Update centre line
  if (handle._centreLine) {
    handle._centreLine.setAttribute("x1", drawBbox.x);
    handle._centreLine.setAttribute("y1", drawBbox.y + drawBbox.height / 2);
    handle._centreLine.setAttribute("x2", drawBbox.x + drawBbox.width);
    handle._centreLine.setAttribute("y2", drawBbox.y + drawBbox.height / 2);
  }

  // Update cursor line
  if (handle.cursor) {
    handle.cursor.setAttribute("x1", bbox.x);
    handle.cursor.setAttribute("y1", bbox.y - handle._cursorOverhang);
    handle.cursor.setAttribute("x2", bbox.x);
    handle.cursor.setAttribute("y2", bbox.y + bbox.height);
  }

  // Redraw static waveform at new scale (if not currently running real-time)
  if (handle._scopeLine && primes(handle) && !handle._scopeRunning) {
    handle._scopeLine.setAttribute("points", staticWavePoints(handle._scopeWave, drawBbox));
  }

  // Update extent line position (only for created extent lines, not SVG-authored)
  if (handle._extentLine && !handle._usingExistingExtent) {
    const extentY = drawBbox.y + drawBbox.height / 2;
    const extent = handle._extent || 200;
    handle._extentY = extentY;

    handle._extentLine.setAttribute("x1", bbox.x + bbox.width);
    handle._extentLine.setAttribute("y1", extentY);
    handle._extentLine.setAttribute("x2", bbox.x + bbox.width + extent);
    handle._extentLine.setAttribute("y2", extentY);

    if (handle._extentHandle) {
      handle._extentHandle.setAttribute("x1", bbox.x + bbox.width + extent);
      handle._extentHandle.setAttribute("y1", extentY - 10);
      handle._extentHandle.setAttribute("x2", bbox.x + bbox.width + extent);
      handle._extentHandle.setAttribute("y2", extentY + 10);
    }

  }

  // If scope is running, the next tick() will use the updated _drawBbox
}
